// ---------------------------------------------------------------------------
// Plugin Slice — runtime plugin registration and per-plugin state management.
//
// Plugin state changes are NOT undoable (no withHistory). Plugin components
// are registered/unregistered from the global componentRegistry as a side
// effect of registerPlugin/unregisterPlugin.
// ---------------------------------------------------------------------------

import { componentRegistry } from '@/features/page-editor/registry/registry'
import type { EditorPlugin, EditorPluginInstance, EditorStoreApi } from '@/features/page-editor/plugins/plugin-types'

export interface PluginSlice {
  plugins: Record<string, EditorPluginInstance>

  registerPlugin: (plugin: EditorPlugin) => void
  unregisterPlugin: (pluginId: string) => void
  getPluginState: <T = unknown>(pluginId: string) => T | undefined
  setPluginState: <T = unknown>(pluginId: string, state: T) => void
}

type Set = (fn: (state: PluginSlice) => void) => void
type Get = () => PluginSlice

export function createPluginSlice(
  set: Set,
  get: Get,
  api: unknown,
): PluginSlice {
  /** Build a minimal EditorStoreApi from the Zustand store API */
  function makeStoreApi(): EditorStoreApi {
    const store = api as {
      getState: () => unknown
      setState: (fn: (state: unknown) => void) => void
      subscribe: (fn: (state: unknown) => void) => () => void
    }
    return {
      getState: () => store.getState(),
      setState: (fn) => store.setState(fn),
      subscribe: (fn) => store.subscribe(fn),
    }
  }

  return {
    plugins: {},

    registerPlugin: (plugin) => {
      // Register plugin components into the global registry
      if (plugin.components) {
        for (const comp of plugin.components) {
          componentRegistry.register(comp)
        }
      }

      // Store plugin instance with initial state
      set((state) => {
        state.plugins[plugin.id] = {
          plugin,
          state: plugin.initialState ? { ...plugin.initialState } : {},
        }
      })

      // Call onInit after registration (guarded so a misbehaving plugin doesn't crash the store)
      try {
        plugin.onInit?.(makeStoreApi())
      } catch (err) {
        console.warn(`[PluginSlice] onInit failed for plugin "${plugin.id}":`, err)
      }
    },

    unregisterPlugin: (pluginId) => {
      const instance = get().plugins[pluginId]
      if (!instance) return

      // Call onDestroy before removal (guarded so a misbehaving plugin doesn't crash the store)
      try {
        instance.plugin.onDestroy?.()
      } catch (err) {
        console.warn(`[PluginSlice] onDestroy failed for plugin "${pluginId}":`, err)
      }

      // Unregister plugin components from the global registry
      if (instance.plugin.components) {
        for (const comp of instance.plugin.components) {
          componentRegistry.unregister(comp.type)
        }
      }

      // Remove plugin from state
      set((state) => {
        delete state.plugins[pluginId]
      })
    },

    getPluginState: <T = unknown>(pluginId: string): T | undefined => {
      const instance = get().plugins[pluginId]
      if (!instance) return undefined
      return instance.state as T
    },

    setPluginState: <T = unknown>(pluginId: string, newState: T) => {
      set((state) => {
        const instance = state.plugins[pluginId]
        if (!instance) return
        instance.state = {
          ...instance.state,
          ...(newState as Record<string, unknown>),
        }
      })
    },
  }
}
